import { Injectable, signal } from '@angular/core';
import { AcceptJsResponse, AcceptJsSecureData, CreditConfiguration, PciOpaqueData } from '@ld.consumer-portal/models';

declare global {
  interface Window {
    Accept?: {
      dispatchData: (secureData: AcceptJsSecureData, responseHandler: (response: AcceptJsResponse) => void) => void;
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class AcceptJsLoaderService {
  readonly isLoaded = signal(false);

  private loadedUrl: string | null = null;

  async loadFromUrl(acceptJsUrl: string): Promise<void> {
    if (!acceptJsUrl) {
      throw new Error('Accept.js URL not provided');
    }

    if (this.isLoaded() && this.loadedUrl === acceptJsUrl) {
      return;
    }

    if (this.loadedUrl && this.loadedUrl !== acceptJsUrl) {
      this.removeScript();
    }

    return new Promise((resolve, reject) => {
      const existingScript = document.querySelector(`script[src="${acceptJsUrl}"]`);
      if (existingScript && window.Accept) {
        this.isLoaded.set(true);
        this.loadedUrl = acceptJsUrl;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.charset = 'utf-8';
      script.src = acceptJsUrl;
      script.id = 'accept-js-script';

      script.onload = () => {
        if (window.Accept) {
          this.isLoaded.set(true);
          this.loadedUrl = acceptJsUrl;
          resolve();
        } else {
          reject(new Error('Accept.js loaded but Accept object not available'));
        }
      };

      script.onerror = () => {
        reject(new Error(`Failed to load Accept.js from ${acceptJsUrl}`));
      };

      document.head.appendChild(script);
    });
  }

  tokenize(
    apiLoginId: string,
    clientKey: string,
    cardNumber: string,
    expirationMonth: string,
    expirationYear: string,
    cvv: string,
  ): Promise<AcceptJsResponse> {
    return new Promise((resolve, reject) => {
      if (!this.isLoaded() || !window.Accept) {
        reject(new Error('Accept.js is not loaded. Call loadFromUrl() first.'));
        return;
      }

      const secureData: AcceptJsSecureData = {
        authData: {
          apiLoginID: apiLoginId,
          clientKey: clientKey,
        },
        cardData: {
          cardNumber: cardNumber.replace(/\s/g, ''),
          month: expirationMonth,
          year: expirationYear,
          cardCode: cvv,
        },
      };

      window.Accept.dispatchData(secureData, (response: AcceptJsResponse) => {
        if (response.messages.resultCode === 'Ok' && response.opaqueData) {
          resolve(response);
        } else {
          const errorMessage = response.messages.message?.[0]?.text || 'Card tokenization failed';
          reject(new Error(errorMessage));
        }
      });
    });
  }

  async tokenizeAll(
    configurations: CreditConfiguration[],
    cardNumber: string,
    expirationMonth: string,
    expirationYear: string,
    cvv: string,
  ): Promise<PciOpaqueData[]> {
    if (!this.isLoaded() || !window.Accept) {
      throw new Error('Accept.js is not loaded. Call loadFromUrl() first.');
    }

    const results: PciOpaqueData[] = [];

    for (const config of configurations) {
      const response = await this.tokenize(
        config.apiLoginId,
        config.apiPublicKey,
        cardNumber,
        expirationMonth,
        expirationYear,
        cvv,
      );

      if (response.opaqueData) {
        results.push({
          providerId: config.providerId,
          opaqueData: response.opaqueData,
        });
      }
    }

    if (results.length === 0) {
      throw new Error('Card tokenization failed for all configurations');
    }

    return results;
  }

  private removeScript(): void {
    const script = document.getElementById('accept-js-script');
    if (script) {
      script.remove();
    }
    this.isLoaded.set(false);
    this.loadedUrl = null;
  }

  detectCardType(cardNumber: string): 'visa' | 'mastercard' | 'amex' | 'discover' | '' {
    const cleaned = cardNumber.replace(/\s/g, '');

    if (/^4/.test(cleaned)) return 'visa';
    if (/^5[1-5]/.test(cleaned) || /^2[2-7]/.test(cleaned)) return 'mastercard';
    if (/^3[47]/.test(cleaned)) return 'amex';
    if (/^6(?:011|5)/.test(cleaned)) return 'discover';

    return '';
  }

  formatCardNumber(cardNumber: string): string {
    const cleaned = cardNumber.replace(/\D/g, '');
    const groups = cleaned.match(/.{1,4}/g);
    return groups ? groups.join(' ') : cleaned;
  }

  maskCardNumber(cardNumber: string): string {
    const cleaned = cardNumber.replace(/\D/g, '');
    if (cleaned.length < 4) return cleaned;
    const last4 = cleaned.slice(-4);
    const masked = '*'.repeat(cleaned.length - 4);
    return this.formatCardNumber(masked + last4);
  }
}
